<template>
	<div class="wall-gallery">
		<div ref="main" class="wall-gallery__main" :class="{ faded }">
			<transition name="fade" mode="out-in">
				<Gallery
					v-if="showGallery"
					ref="gallery"
					layout="wall-gallery"
					wall
					:results="cachedResults"
					@updated="doLayout"
					@view="onView"
				/>
			</transition>
		</div>
		<!-- Loading overlay -->
		<transition name="fade">
			<Loading v-if="loading" color="white" />
		</transition>
		<!-- Submission overlay -->
		<transition name="fade">
			<PolaroidView
				v-if="viewing"
				wall
				:submission="viewing"
				@dismiss="dismiss"
			/>
		</transition>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Loading from '@/components/shared/Loading'
import Gallery from '@/components/main/Gallery'
import PolaroidView from '@/components/main/PolaroidView'

const VIEW_TIMEOUT = 20000
const MAX_RESULTS = 20

export default {
	name: 'WallGallery',
	props: {
		mouse: Object
	},
	data() {
		return {
			cachedResults: [],
			numResults: MAX_RESULTS,
			viewingId: null,
			timeout: null,
			interval: null
		}
	},
	computed: {
		...mapState({
			loading: s => s.gallery.loading,
			fetchedAll: s => s.gallery.done
		}),
		...mapGetters({
			results: 'gallery/results',
			refreshMs: 'config/galleryRefreshMs'
		}),
		showGallery() {
			return this.cachedResults.length
		},
		viewing() {
			return this.cachedResults.find(r => r.id === this.viewingId)
		},
		faded() {
			return Boolean(this.loading || this.viewing)
		}
	},
	methods: {
		...mapActions({
			refreshCampaign: 'campaign/load',
			refreshConfig: 'config/get',
			fetchGallery: 'gallery/fetch'
		}),
		cacheResults() {
			this.cachedResults = [...this.results].slice(0, this.numResults)
		},
		async load() {
			// Refresh config
			await Promise.all([this.refreshConfig(), this.refreshCampaign()])

			// Save last results before force reloading
			const success = await this.fetchGallery({ force: true })
			if (success) {
				this.numResults = MAX_RESULTS
				this.cacheResults()
			}
		},
		doLayout() {
			// Remove results until everything fits into container height
			const h = this.$refs.main.offsetHeight
			const lbh = this.$refs.gallery.$el.offsetHeight
			if (lbh > h && this.numResults > 1) {
				this.numResults--
				this.cacheResults()
			}
		},
		onView(id) {
			this.viewingId = id
			this.setViewTimeout()
		},
		clearViewTimeout() {
			window.clearTimeout(this.timeout)
		},
		setViewTimeout() {
			this.clearViewTimeout()
			this.timeout = window.setTimeout(this.dismiss, VIEW_TIMEOUT)
		},
		dismiss() {
			this.viewingId = null
		}
	},
	watch: {
		mouse() {
			if (this.viewingId !== null) this.setViewTimeout()
		},
		refreshMs(next) {
			window.clearInterval(this.interval)
			this.interval = window.setInterval(this.load, next)
		}
	},
	created() {
		this.load()
		// Set up loading interval
		this.interval = window.setInterval(this.load, this.refreshMs)
	},
	beforeDestroy() {
		this.clearViewTimeout()
		window.clearInterval(this.interval)
	},
	components: {
		Loading,
		Gallery,
		PolaroidView
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/transitions';
@import '~scss/mq';

@include fade($spd-wall);

.wall-gallery {
	@extend %flex-col;
	flex-grow: 1;
	max-height: 100vh;

	&__main {
		@extend %flex-center;
		flex-grow: 1;
		overflow: hidden;
		margin: 1rem;
		justify-content: stretch;
		transition: opacity $spd-wall;
		&.faded {
			opacity: 0.25;
		}
	}

	&__view {
		@extend %abs-full;
	}

	@include mq(md) {
		&__main {
			margin: 1.5rem;
		}
	}
}
</style>
